/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../DeprecatedConditionalFormattingAPI',
	'../../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'underscore',
	'../../../../../widgets/livewidget/nls/StringResources',
	'jquery',
	'./DeprecatedConditionalFormattingAPISpec'
], (DeprecatedConditionalFormattingAPI, APIFactory, _, StringResources, $, DeprecatedConditionalFormattingAPISpec) => {


	class DeprecatedConditionalFormatting extends DeprecatedConditionalFormattingAPISpec {
		constructor(options) {
			super();
			this._content = options.content;
			this._dashboardApi = options.dashboardAPI;
			this._selectedRangeIndex = 0;
			this._colorsFeature = options.features['Dashboard.Colors'];

			this.__apiInstance = APIFactory.createAPI(this, [DeprecatedConditionalFormattingAPI]);

			if (!this._isNewConditionalFormatFeature()) {
				this._paletteFeature = options.features['Dashboard.Palette'];

				this._properties = options.features['Properties'];
				this._properties.registerProvider(this.__apiInstance);

				this._customColor = options.features['Dashboard.CustomColor'];
				this._customColor.registerProvider(this.__apiInstance);
				this._registerEvents();
			}

			this.ICON_MAP = new Map();
			this.ICON_MAP.set('upArrow', { id: 'conditionalFormatting-arrow-up_16', label:  StringResources.get('conditionalFormattingIconArrowUp') });
			this.ICON_MAP.set('rightArrow', { id: 'conditionalFormatting-arrow-right_16', label:  StringResources.get('conditionalFormattingIconArrowRight') });
			this.ICON_MAP.set('downArrow', { id: 'conditionalFormatting-arrow-down_16', label:  StringResources.get('conditionalFormattingIconArrowDown') });
			this.ICON_MAP.set('upCaret', { id: 'conditionalFormatting-caret-up_16', label:  StringResources.get('conditionalFormattingIconCaretUp') });
			this.ICON_MAP.set('dash', { id: 'conditionalFormatting-dash_16', label:  StringResources.get('conditionalFormattingIconDash') });
			this.ICON_MAP.set('downCaret', { id: 'conditionalFormatting-caret-down_16', label:  StringResources.get('conditionalFormattingIconCaretDown') });
			this.ICON_MAP.set('checkmark', { id: 'conditionalFormatting-checkmark_16', label:  StringResources.get('conditionalFormattingIconCheckmark') });
			this.ICON_MAP.set('warning', { id: 'conditionalFormatting-warning_16', label:  StringResources.get('conditionalFormattingIconWarning') });
			this.ICON_MAP.set('xSymbol', { id: 'conditionalFormatting-x-symbol_16', label:  StringResources.get('conditionalFormattingIconXSymbol') });
			this.ICON_MAP.set('circle', { id: 'conditionalFormatting-circle_16', label:  StringResources.get('conditionalFormattingIconCircle') });
			this.ICON_MAP.set('triangle', { id: 'conditionalFormatting-triangle_16', label:  StringResources.get('conditionalFormattingIconTriangle') });
			this.ICON_MAP.set('diamond', { id: 'conditionalFormatting-diamond_16', label:  StringResources.get('conditionalFormattingIconDiamond') });
			this.ICON_MAP.set('satisfied', { id: 'conditionalFormatting-face-satisfied_16', label:  StringResources.get('conditionalFormattingIconFaceSatisfied') });
			this.ICON_MAP.set('neutral', { id: 'conditionalFormatting-face-neutral_16', label:  StringResources.get('conditionalFormattingIconFaceNeutral') });
			this.ICON_MAP.set('dissatisfied', { id: 'conditionalFormatting-face-dissatisfied_16', label:  StringResources.get('conditionalFormattingIconFaceDissatisfied') });
			this.ICON_MAP.set('none', { id: 'conditionalFormatting-noshape_16', label:  StringResources.get('conditionalFormattingIconNone') });

		}

		isEnabled() {
			const visualization = this._content.getFeature('Visualization');
			return visualization && visualization.getDefinition().getId() === 'kpi';
		}

		getAPI() {
			return this.__apiInstance;
		}

		_registerEvents() {
			this._paletteFeature.on('palette:deleted', this._onPaletteDeleted, this);
			this._paletteFeature.on('palette:updated', this._onPaletteUpdated, this);
		}

		_deregisterEvents() {
			this._paletteFeature.off('palette:deleted', this._onPaletteDeleted, this);
			this._paletteFeature.off('palette:updated', this._onPaletteUpdated, this);
		}

		destroy() {
			if (!this._isNewConditionalFormatFeature()) {
				this._deregisterEvents();

				if (this._properties) {
					this._properties.deregisterProvider(this.__apiInstance);
				}

				if (this._customColor) {
					this._customColor.deregisterProvider(this.__apiInstance);
				}
			}
		}

		/**
		 * Always return a copy of the default spec to make sure nobody modifies it
		 */
		static _getDefaultConditionalFormatting() {
			return JSON.parse(JSON.stringify({
				enabled: true,
				paletteId: 'condColorPalette0',
				paletteReversed: false,
				rangeScale: DeprecatedConditionalFormatting.RANGE_SCALE_PERCENTAGE,
				ranges: [{
					startValue: null,
					endValue: 100,
					shape: 'downArrow'
				}, {
					startValue: 100,
					endValue: null,
					shape: 'upArrow'
				}]
			}));
		}

		getCustomColorList() {
			let usedColors = [];
			let ranges = this.getRanges();
			ranges.forEach((range) => {
				if(range && range.userSetColorId && this._colorsFeature.isCustomColor(range.userSetColorId)) {
					let hexValue = this._colorsFeature.getHexColorFromClassName(range.userSetColorId);
					usedColors.push(hexValue);
				}
			});
			return usedColors;
		}

		/**
		 * Initializing all the information needed to conditionally apply formatting
		 */
		initializeFeature() {
			// // This is temporary (and bad).  Needed until a feature can contribute its own model
			if (!this._contentModel) {
				const layoutModel = this._dashboardApi.getFeature('internal').getBoardModel().layout.findModel(this._content.getId());
				if (layoutModel) {
					this._contentModel = this._dashboardApi.getFeature('internal').getBoardModel().layout.findModel(this._content.getId()).getContentModel();
				}
			}

			if (!this._conditionalFormatting) {
				// Get the conditional formatting info from the model and default it if the model is empty
				const existingConditionalFormatting = this._contentModel ?  this._contentModel.getConditionalFormatting() : {};
				this._conditionalFormatting = Object.assign(DeprecatedConditionalFormatting._getDefaultConditionalFormatting(), existingConditionalFormatting);

				// Load the palette information
				return this._processConditionalPalette();
			}

			return Promise.resolve();
		}

		/**
		 * Gets the colors for the palette and applies them to the formats
		 * @return	{Promise}
		 */
		_processConditionalPalette() {
			// Load the palette information and the needed svg icons
			return this._colorsFeature.getPaletteColors({
				paletteId: this.getPaletteId(),
				type: 'ConditionalPalette',
				defaultIfNotFound: true,
				addMissingBaseColors: false
			}).then(paletteColors => {
				this._paletteColors = paletteColors;
				this._applyPaletteToRanges();
			});
		}

		// FIXME: This method is trigged from an event. I don't see a reason it needs to return any promise
		_onPaletteDeleted({ paletteId }) {
			if (paletteId === this.getPaletteId()) {
				return this.setPaletteId(this._colorsFeature.getDefaultPaletteName('ConditionalPalette'));
			}
			return Promise.resolve();
		}

		// FIXME: This method is trigged from an event. I don't see a reason it needs to return any promise
		_onPaletteUpdated({ paletteId }) {
			if (paletteId === this.getPaletteId()) {
				return this.setPaletteId(paletteId);
			}
			return Promise.resolve();
		}

		_onConditionalFormattingChange(options = {}) {
			// Temporary until changes to the undoRedoController to call APIs on undo/redo instead
			// of making model changes directly
			if (options.sender === 'UndoRedoController') {
				this._conditionalFormatting = null;
			}
		}

		getFormatting(baseValue, targetValue) {
			this.setBaseValue(baseValue);
			this.setTargetValue(targetValue);

			if (!this.getCondtionalFormattingEnabled() || !$.isNumeric(this._baseValue) || !$.isNumeric(this._targetValue)) {
				return null;
			}

			const range = this._findRangeForValue();

			if (range) {
				const colorId = this._getColorIdForRangeObject(range);
				return {
					color: this._getHexFromColorId(colorId),
					shapeId: this._getShapeIdForRangeObject(range)
				};
			}

			return null;
		}

		setBaseValue(baseValue) {
			this._baseValue = baseValue;
		}

		setTargetValue(targetValue) {
			this._targetValue = targetValue;
		}

		/**
		 * Finds the range that has conditions that all pass with the provided value
		 * @return {Object} The format that has conditions that all pass with the value
		 */
		_findRangeForValue() {
			return this._getRanges().find((range) => {
				let value = this._baseValue;
				if (this.getRangeScale() === DeprecatedConditionalFormatting.RANGE_SCALE_PERCENTAGE) {
					value = this._calculatePercentValue(this._baseValue, this._targetValue);
					if (value === null) {
						return false;
					}
				}

				// Case for a single possible value range
				if (range.startValue === range.endValue && range.endValue === value) {
					return true;
				}

				if (range.startValue === null || value >= range.startValue) {
					if (range.endValue === null || value < range.endValue) {
						return true;
					}
				}

				return false;
			});
		}

		/**
		 * Calculates the percentage of two numbers
		 * @param {Number} baseValue The base value to use to find the correct formating information
		 * @param {Number} targetValue The target value.
		 * @return {Number} The percentage
		 */
		_calculatePercentValue(baseValue, targetValue) {
			if (baseValue === 0 && targetValue === 0) {
				return 0;
			} else {
				// We can't do any conditional formatting for a % condition when the target is zero
				if (targetValue === null || !$.isNumeric(targetValue) || targetValue === 0) {
					return null;
				}

				// To get the percentage, calculate the percent difference
				return ((baseValue - targetValue) / Math.abs(targetValue)) * 100;
			}
		}

		/**
		 * Sets the paletteColorHexValue property for the range object using the palette information.  Note,
		 * the paletteColorHexValue property is runtime only and will not be saved in the model
		 */
		_applyPaletteToRanges() {
			let colorIndexMap = this._buildIndexMap(this._paletteColors.length, this._getRanges().length);

			if (!this._getPaletteReversed()) {
				colorIndexMap.reverse();
			}

			// Todo Post R3.  If the indexMap length is smaller then formats.length then that
			// means we don't have enough colors in our palette.  Do we remove format?? Not an
			// issue for R3 since those properties are not exposed.

			const paletteLength = this._paletteColors.length;


			// Add the appropriate hex value to each format
			this._getRanges().forEach((range, index) => {
				if (index < paletteLength) {
					range.defaultColorId = this._paletteColors[colorIndexMap[index]].name;
				} else {
					range.defaultColorId = this._paletteColors[colorIndexMap[index % paletteLength]].name;
				}
			});
		}

		/**
		 * Using the size of the palette and number of formats defined, find an appropriate mapping of which color
		 * should be used for which format
		 * @return {Array}
		 */
		_buildIndexMap(paletteLength, formatsLength) {
			if (!paletteLength || !formatsLength) {
				return [];
			}

			// If we have more formats then we have colors just return a map for the size of the palette
			if (paletteLength <= formatsLength) {
				return _.range(paletteLength);
			}

			// For backwards compatibility keep the same mapping for anything with 5 colors and less.
			if (paletteLength === 5) {
				const fiveColorPalleteMaps = [
					[4], // one color
					[0, 4], // two colors
					[0, 2, 4], // three colors
					[0, 1, 2, 4], // four colors
					[0, 1, 2, 3, 4] // five colors
				];
				return fiveColorPalleteMaps[formatsLength-1];
			}

			// Only asking for one color, grab the middle one
			if (formatsLength === 1) {
				return [Math.floor(paletteLength / 2)];
			}

			const step = (paletteLength - 1) / formatsLength;
			const indexMap = [0];
			for (let i = 2; i < formatsLength; i++) {
				indexMap.push(Math.floor(step * i));
			}
			indexMap.push(paletteLength - 1);

			return indexMap;
		}

		_shouldShowProperties() {
			return this.getCondtionalFormattingEnabled() && this._hasTargetValue();
		}

		_hasTargetValue() {
			return $.isNumeric(this._targetValue);
		}

		getCondtionalFormattingEnabled() {
			return this._conditionalFormatting ? this._conditionalFormatting.enabled : false;
		}

		setConditionalFormattingEnabled(enabled, setOptions) {
			this._conditionalFormatting.enabled = enabled;
			this._updateConditionalFormattingProperty(setOptions);
		}

		getPaletteId() {
			return this._conditionalFormatting && this._conditionalFormatting.paletteId;
		}

		setPaletteId(paletteId, setOptions) {
			this._conditionalFormatting.paletteId = paletteId;
			return this._processConditionalPalette()
				.then(this._updateConditionalFormattingProperty.bind(this, setOptions));
		}

		_getPaletteReversed() {
			return this._conditionalFormatting && this._conditionalFormatting.paletteReversed;
		}

		_setPaletteReversed(reversed) {
			this._conditionalFormatting.paletteReversed = reversed;
			this._applyPaletteToRanges();
		}

		reverseColors() {
			const ranges = this._getRanges();
			const midIndex = Math.floor(ranges.length / 2);
			const rangesLength = ranges.length;

			// Made sure we flip any user defined colors
			for (let i=0; i < midIndex; i++) {
				let temp = ranges[i].userSetColorId;
				ranges[i].userSetColorId = ranges[rangesLength - i - 1].userSetColorId;
				ranges[rangesLength - i - 1].userSetColorId = temp;
			}

			this._setPaletteReversed(!this._getPaletteReversed());
			this._updateConditionalFormattingProperty();
		}

		/**
		 * Return a copy of our ranges array.  We return a copy since we don't want the outside world to modify this
		 * array directly, they need to go through public APIs to make changes to the conditional formatting.  This
		 * API is mostly used for property rendering.
		 */
		getRanges() {
			return JSON.parse(JSON.stringify(this._getRanges()));
		}

		_getRanges() {
			return this._conditionalFormatting && this._conditionalFormatting.ranges || [];
		}

		getRangeScale() {
			return this._conditionalFormatting && this._conditionalFormatting.rangeScale;
		}

		setRangeScale(rangeScale, setOptions) {
			if (rangeScale !== DeprecatedConditionalFormatting.RANGE_SCALE_PERCENTAGE && rangeScale !== DeprecatedConditionalFormatting.RANGE_SCALE_NUMERIC) {
				this._dashboardApi.getFeature('Logger').error('Invalid conditional range scale', rangeScale);
				return;
			}

			if (rangeScale === this.getRangeScale()) {
				return;
			}

			this._updateRangeScale(rangeScale);

			this._updateConditionalFormattingProperty(setOptions);
		}

		_updateRangeScale(newRangeScale) {
			this._conditionalFormatting.rangeScale = newRangeScale;

			// If we don't have info on the target value yet there's not much we can do
			// when switching the range scale, so leave the values alone
			if (this._targetValue !== null && this._targetValue !== undefined) {
				const getNewValue = (value) => {
					if (value === null) {
						return value;
					} else if (newRangeScale === DeprecatedConditionalFormatting.RANGE_SCALE_NUMERIC) {
						return Math.abs(this._targetValue) * (value / 100);
					} else {
						return (value / Math.abs(this._targetValue)) * 100;
					}
				};

				this._getRanges().forEach((range) => {
					range.startValue = getNewValue(range.startValue);
					range.endValue = getNewValue(range.endValue);
				});
			}
		}

		getNumberOfRanges() {
			return this._getRanges().length;
		}

		setNumberOfRanges(numberOfRanges, setOptions) {
			if (!$.isNumeric(numberOfRanges) || numberOfRanges < 1) {
				this._dashboardApi.getFeature('Logger').error('Invalid number of ranges', numberOfRanges);
				return;
			}

			let currentNumberOfRanges = this.getNumberOfRanges();
			if (this.getNumberOfRanges() < numberOfRanges) {
				this._addNewRanges(numberOfRanges - currentNumberOfRanges);
			} else {
				this._removeRanges(currentNumberOfRanges - numberOfRanges);
			}

			this._applyPaletteToRanges();
			this._updateConditionalFormattingProperty(setOptions);
		}

		_addNewRanges(numberOfRangesToAdd) {
			for (let i=0; i < numberOfRangesToAdd; i++) {
				let indexWhereToInsert = this._findIndexWhereToInsert();
				let ranges = this._getRanges();
				let rangeToSplit = ranges[indexWhereToInsert];
				let newRangeStartValue = this._findNewRangeStartValue(rangeToSplit);

				let newRange = {
					startValue: newRangeStartValue
				};

				if (rangeToSplit.endValue !== null && rangeToSplit.endValue !== newRangeStartValue) {
					newRange.endValue = rangeToSplit.endValue;
				} else {
					newRange.endValue = null;
				}

				// Set the current range endValue to match the new ranges startValue - we should never have gaps between ranges
				rangeToSplit.endValue = newRangeStartValue;

				ranges.splice(indexWhereToInsert, 1, rangeToSplit, newRange);
			}
		}

		/**
		 * Given a range that we want to 'split' so that we can insert a new range, find the startValue of new range (split value)
		 * @param {Object} rangeToSplit The range that we want to split into two ranges
		 * @returns {Integer}
		 */
		_findNewRangeStartValue(rangeToSplit) {
			let newRangeStartValue = 0;
			// If the current range has an null (max) end value, then we need to find a suitable new end value for it
			if (rangeToSplit.endValue === null) {
				if (this.getNumberOfRanges() === 1) {
					// Currently only one range, so set it up so that we have lower and higher then target ranges
					if (this.getRangeScale() === DeprecatedConditionalFormatting.RANGE_SCALE_NUMERIC) {
						if (this._targetValue < rangeToSplit.startValue) {
							newRangeStartValue = rangeToSplit.startValue < 0 ? rangeToSplit.startValue / 2 : rangeToSplit.startValue * 2;
						} else {
							newRangeStartValue = this._targetValue;
						}
					} else if (this.getRangeScale() === DeprecatedConditionalFormatting.RANGE_SCALE_PERCENTAGE) {
						if (rangeToSplit.startValue > 0) {
							newRangeStartValue = rangeToSplit.startValue * 2;
						} else {
							newRangeStartValue = 0;
						}
					}
				} else {
					// No way to know how much to jump from the left range start value, so 25% or half the target
					newRangeStartValue = rangeToSplit.startValue + (this.getRangeScale() === DeprecatedConditionalFormatting.RANGE_SCALE_PERCENTAGE ?  25 : Math.abs((this._targetValue / 2)));
				}
			} else {
				// If we're going from 1 range to 2, simply add a new range where it's startValue is the current range endValue
				if (this.getNumberOfRanges() === 1) {
					newRangeStartValue = rangeToSplit.endValue;
				} else {
					if (rangeToSplit.startValue === null) {
						newRangeStartValue = rangeToSplit.endValue / 2;
					} else {
						newRangeStartValue = rangeToSplit.startValue + ((rangeToSplit.endValue - rangeToSplit.startValue) / 2);
					}
				}
			}

			return newRangeStartValue;
		}

		_findIndexWhereToInsert() {
			let indexToInsert = 0;
			let lastBiggestRangeSize = 0;
			this.getRanges().forEach((range, index) => {
				if (range.startValue !== null && range.endValue !== null) {
					const rangeSize = range.endValue - range.startValue;
					if (rangeSize >= lastBiggestRangeSize) {
						indexToInsert = index;
						lastBiggestRangeSize = rangeSize;
					}
				}
			});

			return indexToInsert;// === 0 ? 0 : indexToInsert - 1;
		}

		_removeRanges(numberOfRangesToRemove) {
			for (let i=0; i < numberOfRangesToRemove; i++) {
				let indexToRemove = Math.floor(this.getNumberOfRanges() / 2);
				this._removeRange(indexToRemove);
			}
		}

		_removeRange(rangeIndexToRemove) {
			if (!$.isNumeric(rangeIndexToRemove) || rangeIndexToRemove < 0 || rangeIndexToRemove > this.getNumberOfRanges()) {
				this._dashboardApi.getFeature('Logger').error('Invalid index of range to remove', rangeIndexToRemove);
				return null;
			} else if (this.getNumberOfRanges() === 1) {
				this._dashboardApi.getFeature('Logger').error('Cannot remove the last range', rangeIndexToRemove);
				return null;
			}
			const removingEdgeRange = rangeIndexToRemove === 0 || rangeIndexToRemove === (this.getNumberOfRanges() - 1);

			const removedRange = this._getRanges().splice(rangeIndexToRemove, 1)[0];

			let ranges = this._getRanges();

			// IF we're removing an inside range, fix up the start and end values for the
			// neighbouring ranges.  Also fix the values if we're going down to one range
			if (!removingEdgeRange) {
				if (rangeIndexToRemove > 0) {
					ranges[rangeIndexToRemove - 1].endValue = removedRange.endValue;
				} else {
					ranges[rangeIndexToRemove].startValue = removedRange.startValue;
				}
			}

			return removedRange;
		}

		removeRange(rangeIndexToRemove, setOptions) {
			if (this._removeRange(rangeIndexToRemove)) {
				this._updateConditionalFormattingProperty(setOptions);
			}
		}

		resetRanges() {
			const originalNumberOfRanges = this.getNumberOfRanges();
			const originalRangeScale = this.getRangeScale();

			// Get the default ranges
			this._conditionalFormatting.rangeScale = DeprecatedConditionalFormatting.RANGE_SCALE_PERCENTAGE;
			this._conditionalFormatting.ranges = DeprecatedConditionalFormatting._getDefaultConditionalFormatting().ranges;

			const setOptions = {
				payloadData: {
					undoRedoTransactionId: _.uniqueId('_resetRanges_')
				}
			};
			this._setPaletteReversed(false);
			this.setRangeScale(originalRangeScale, setOptions);
			this.setNumberOfRanges(originalNumberOfRanges, setOptions);
			this._updateConditionalFormattingProperty(setOptions);
		}

		/**
		 * We only expose one property in the model as a big JSON.  This mehod will update that
		 * JSON with the latest information found in the _conditionalFormattingProperty memeber property
		 */
		_updateConditionalFormattingProperty(setOptions) {
			const conditionalFormattingClone = JSON.parse(JSON.stringify(this._conditionalFormatting));

			// There are a few runtime properties that we calculate on open.  Make sure those don't get persisted in
			// our model
			conditionalFormattingClone.ranges.forEach((range) => {
				delete range.defaultColorId;
			});

			this._contentModel.setConditionalFormatting(conditionalFormattingClone, setOptions);
		}

		/**
		 * Returns the currently selected range index - used for the properties UI
		 * @return {Integer}
		 */
		getSelectedRangeIndex() {
			if (this._selectedRangeIndex >= this.getNumberOfRanges()) {
				this._selectedRangeIndex = this.getNumberOfRanges() - 1;
			}
			return this._selectedRangeIndex;
		}

		/**
		 * Sets the currently selected range index
		 * @param {Integer} rangeIndex
		 */
		setSelectedRangeIndex(rangeIndex) {
			if (rangeIndex < 0) {
				this._selectedRangeIndex = 0;
			} else {
				this._selectedRangeIndex = rangeIndex >= this.getNumberOfRanges() ? this.getNumberOfRanges() - 1 : rangeIndex;
			}
		}

		_checkValidValueForRange(value, isStartValue, rangeIndex) {
			const range = this._getRangeFromIndex(rangeIndex);
			if (!range) {
				this._dashboardApi.getFeature('Logger').error('Invalid index of range.', rangeIndex);
				return this._buildValidateResponse(false, StringResources.get('conditionalFormattingStepIndexInvalid'));
			}

			let validBounds = {};
			if (isStartValue) {
				validBounds.start = rangeIndex === 0 ? null : this._getRangeFromIndex(rangeIndex - 1).startValue;
				validBounds.end = range.endValue;
			} else {
				validBounds.start = range.startValue;
				validBounds.end = rangeIndex === (this.getNumberOfRanges() - 1) ? null : this._getRangeFromIndex(rangeIndex + 1).endValue;
			}

			// Trying to set min / max
			if (value === null) {
				// Trying to set the start and end to min/max with more then one range is not allowed
				if (this.getNumberOfRanges() > 1 && validBounds.start === null && validBounds.end === null) {
					return this._buildValidateResponse(false, StringResources.get('conditionalFormattingNonNumericValue'));
				}

				// Only allow min for the first range and max for the last range
				if ((isStartValue && rangeIndex !== 0) || (!isStartValue && rangeIndex !== (this.getNumberOfRanges() - 1))) {
					return this._buildValidateResponse(false, StringResources.get('conditionalFormattingNonNumericValue'));
				}

				if ((isStartValue && value !== validBounds.start) || (!isStartValue && value !== validBounds.end)) {
					return this._buildValidateResponse(false, this._getOutOfBoundsErrorMessage(validBounds));
				}

				return this._buildValidateResponse(true);
			}

			if (!$.isNumeric(value)) {
				return this._buildValidateResponse(false, StringResources.get('conditionalFormattingNonNumericValue'));
			}

			// The UI passes us a string
			const valueToValidate = parseFloat(value);
			if ((validBounds.start !== null && valueToValidate < validBounds.start) || (validBounds.end !== null && valueToValidate > validBounds.end)) {
				return this._buildValidateResponse(false, this._getOutOfBoundsErrorMessage(validBounds));
			}

			return this._buildValidateResponse(true);
		}

		_getOutOfBoundsErrorMessage(validBounds) {
			if (validBounds.start === null) {
				return StringResources.get('conditionalFormattingValueShouldBeLower', {
					value: validBounds.end
				});
			} else if (validBounds.end === null) {
				return StringResources.get('conditionalFormattingValueSouldBeHigher', {
					value: validBounds.start
				});
			}

			return StringResources.get('conditionalFormattingValueOutOfBoundsInvalid', {
				start: validBounds.start,
				end: validBounds.end
			});
		}

		checkValidStartValueForRange(startValue, rangeIndex) {
			return this._checkValidValueForRange(startValue, true, rangeIndex);
		}

		checkValidEndValueForRange(endValue, rangeIndex) {
			return this._checkValidValueForRange(endValue, false, rangeIndex);
		}

		getStartValueForRange(rangeIndex) {
			const range = this._getRangeFromIndex(rangeIndex);
			if (!range) {
				return null;
			}

			return range.startValue;
		}

		setStartValueForRange(startValue, rangeIndex, setOptions) {
			const validInfo = this.checkValidStartValueForRange(startValue, rangeIndex);
			if (!validInfo.isValid) {
				return validInfo;
			}

			const range = this._getRangeFromIndex(rangeIndex);

			if (startValue === null) {
				range.startValue = null;
			} else {
				// Make sure we save a number in the spec
				const newStartValue = parseFloat(startValue);
				range.startValue = newStartValue;
				if (rangeIndex !== 0) {
					const rangeToLeft = this._getRangeFromIndex(rangeIndex - 1);
					if (rangeToLeft) {
						rangeToLeft.endValue = newStartValue;
					}
				}
			}

			this._updateConditionalFormattingProperty(setOptions);

			return this._buildValidateResponse(true);
		}

		getEndValueForRange(rangeIndex) {
			const range = this._getRangeFromIndex(rangeIndex);
			if (!range) {
				return null;
			}

			return range.endValue;
		}

		setEndValueForRange(endValue, rangeIndex, setOptions) {
			const validInfo = this.checkValidEndValueForRange(endValue, rangeIndex);
			if (!validInfo.isValid) {
				return validInfo;
			}

			const range = this._getRangeFromIndex(rangeIndex);

			if (endValue === null) {
				range.endValue = null;
			} else {
				// Make sure we save a number in the spec
				const newEndValue = parseFloat(endValue);
				range.endValue = newEndValue;
				if ((rangeIndex + 1) < this.getNumberOfRanges()) {
					const rangeToRight = this._getRangeFromIndex(rangeIndex + 1);
					if (rangeToRight) {
						rangeToRight.startValue = newEndValue;
					}
				}
			}

			this._updateConditionalFormattingProperty(setOptions);

			return this._buildValidateResponse(true);
		}

		_buildValidateResponse(isValid, message) {
			return {
				isValid: isValid,
				message: message
			};
		}

		_getRangeFromIndex(rangeIndex) {
			const range = this._getRanges()[rangeIndex];
			if (!range) {
				this._dashboardApi.getFeature('Logger').error(`Could not find range with index ${rangeIndex}`);
				return null;
			}

			return range;
		}

		_findPaletteColorFromColorId(colorId) {
			return _.find(this._paletteColors, (paletteColor) => {
				return paletteColor.name === colorId;
			});
		}

		_getHexFromColorId(colorId) {
			const paletteColor = this._findPaletteColorFromColorId(colorId);
			const paletteColorHex = paletteColor ? paletteColor.hexValue : undefined;
			const customColorHex = this._colorsFeature.getHexColorFromClassName(colorId);
			let colorHex = paletteColorHex || customColorHex;

			// Default to the last color in the palette if we didn't find the color that was set.  This
			// can happen when switching to a palette with less colors
			if (!colorHex && this._paletteColors.length > 0) {
				return this._paletteColors[this._paletteColors.length-1].hexValue;
			}

			return colorHex ? colorHex : null;
		}

		_getColorIdForRangeObject(range) {
			// Only return the users color Id if it's still valid.  It could be invalid if we
			// switched to a palette with less colors.
			let validColor = range.userSetColorId && (this._findPaletteColorFromColorId(range.userSetColorId) || this._colorsFeature.getHexColorFromClassName(range.userSetColorId));
			if (validColor) {
				return range.userSetColorId;
			}

			return range.defaultColorId;
		}

		setColorIdForRange(colorId, rangeIndex, setOptions) {
			const range = this._getRangeFromIndex(rangeIndex);
			if (!range) {
				return;
			}

			range.userSetColorId = colorId;

			this._updateConditionalFormattingProperty(setOptions);
		}

		getColorHexForRange(rangeIndex) {
			const colorId = this.getColorIdForRange(rangeIndex);

			if (!colorId) {
				return null;
			}

			return this._getHexFromColorId(colorId);
		}

		getColorIdForRange(rangeIndex) {
			const range = this._getRangeFromIndex(rangeIndex);
			if (!range) {
				return null;
			}
			return this._getColorIdForRangeObject(range);
		}

		/**
		 * Return a copy.  We return a copy since we don't want the outside world to modify this object directly.
		 * They need to go through public APIs to make changes to the conditional formatting.
		 */
		getConditionalFormatting() {
			return JSON.parse(JSON.stringify(this._conditionalFormatting));
		}

		setConditionalFormatting(conditionalFormatting = null, setOptions) {
			if (conditionalFormatting === null) {
				this._conditionalFormatting = DeprecatedConditionalFormatting._getDefaultConditionalFormatting();
			} else {
				// Object.assign will use references for any properties that are objects (like conditionalFormatting.ranges).  Make a copy of conditionalFormatting so that we don't have references
				// to external objects which might come from our undo/redo stack.
				this._conditionalFormatting = Object.assign(DeprecatedConditionalFormatting._getDefaultConditionalFormatting(), this._conditionalFormatting, JSON.parse(JSON.stringify((conditionalFormatting))));
			}

			return this._processConditionalPalette()
				.then(this._updateConditionalFormattingProperty.bind(this, setOptions));
		}

		getShapeList() {
			const shapes = [];
			this.ICON_MAP.forEach((icon, key) => { shapes.push(key); });
			return shapes;
		}

		getShapeForRange(rangeIndex) {
			const range = this._getRangeFromIndex(rangeIndex);
			if (!range) {
				this._dashboardApi.getFeature('Logger').error(`Could not find range with index ${rangeIndex}`);
				return null;
			}
			return range.shape || 'none';
		}

		setShapeForRange(shape, rangeIndex) {
			if (!this.ICON_MAP.has(shape)) {
				this._dashboardApi.getFeature('Logger').error(`Invalid shape name: ${shape}`);
				return;
			}
			const range = this._getRangeFromIndex(rangeIndex);
			if (!range) {
				this._dashboardApi.getFeature('Logger').error(`Could not find range with index ${rangeIndex}`);
				return;
			}
			range.shape = shape;

			this._updateConditionalFormattingProperty();
		}

		getShapeDefinition(shape) {
			if (!shape || !this.ICON_MAP.has(shape)) {
				return null;
			}
			return this.ICON_MAP.get(shape);
		}

		_getShapeIdForRangeObject(range) {
			if (!range.shape || range.shape === 'none') {
				return undefined;
			}
			const shapeDef = this.getShapeDefinition(range.shape);
			return shapeDef ? shapeDef.id : undefined;
		}

		getPropertyLayoutList() {
			let propertyLayoutList = [{
				id: 'color',
				type: 'Section',
				label: StringResources.get('sectionName_colorPalette'),
				position: 1
			}, {
				id: 'conditionalColors',
				editor: {
					sectionId: 'visualization.color',
					position: 2,
					uiControl: {
						type: 'Separator'
					}
				}
			}, {
				id: 'range',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('sectionName_range')
			}, {
				id: 'conditionalFormattingEnabled',
				editor: {
					sectionId: 'visualization.color',
					readOnly: !this._hasTargetValue(),
					position: 3,
					uiControl: {
						type: 'ToggleButton',
						label: StringResources.get('conditionalFormattingEnabled'),
						value: this.getCondtionalFormattingEnabled(),
						onChange: (propertyName, propertyValue) => {
							this.getAPI().setConditionalFormattingEnabled(propertyValue);
						}
					}
				}
			}];

			if (this._shouldShowProperties()) {
				propertyLayoutList.push({
					'id': 'conditionalPalette',
					editor: {
						sectionId: 'visualization.color',
						position: 4,
						uiControl: {
							type: 'NewPalette',
							sectionLabel: StringResources.get('currentConditionalPalette'),
							newPaletteLabel: StringResources.get('propNewColorPaletteLabel'),
							linkLabel: StringResources.get('propColorPaletteChangeLabel'),
							ariaLabel: StringResources.get('propColorPaletteChangeLabel'),
							includePatternPalettes: false,
							paletteType: 'ConditionalPalette',
							allowPaletteReverse: false,
							open: false,
							enableReorder: false,
							sectionPosition:  1,
							onChangePalette: (propertyName, paletteItem) => {
								this.getAPI().setPaletteId(paletteItem.id);
							},
							getPaletteId: this.getPaletteId.bind(this),
							isReversed: () => false
						}
					}
				});

				propertyLayoutList.push({
					id: 'rangeScale',
					editor: {
						sectionId: 'visualization.color.range',
						position: 5,
						uiControl: {
							type: 'DropDown',
							label: StringResources.get('rangeScaleLabel'),
							options: [{
								label: StringResources.get('rangeScaleNumeric'),
								value: DeprecatedConditionalFormatting.RANGE_SCALE_NUMERIC
							}, {
								label: StringResources.get('rangeScalePercentage'),
								value: DeprecatedConditionalFormatting.RANGE_SCALE_PERCENTAGE
							}],
							value: this.getRangeScale(),
							onChange: (propertyName, propertyValue) => {
								this.getAPI().setRangeScale(propertyValue);
							}
						}
					}
				});

				propertyLayoutList.push({
					id: 'numberOfRanges',
					editor: {
						sectionId: 'visualization.color.range',
						position: 6,
						uiControl: {
							type: 'DropDown',
							label: StringResources.get('numberOfSteps'),
							options: [{
								label: '1',
								value: 1
							}, {
								label: '2',
								value: 2
							}, {
								label: '3',
								value: 3
							}],
							value: this.getNumberOfRanges(),
							onChange: (propertyName, propertyValue) => {
								this.getAPI().setNumberOfRanges(propertyValue);
							}
						}
					}
				});

				propertyLayoutList.push({
					id: 'conditionalFormatting',
					editor: {
						sectionId: 'visualization.color.range',
						position: 7,
						uiControl: {
							module: 'dashboard-analytics/features/widget/conditionalFormatting/properties/ConditionalStepsProperty',
							conditionalFormattingFeature: this.getAPI(),
							dashboardApi: this._dashboardApi,
							paletteColors: this._paletteColors
						}
					}
				});
			}

			return propertyLayoutList;
		}

		_isNewConditionalFormatFeature () {
			return !this._dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'condFormat', 'disabled');
		}
	}

	DeprecatedConditionalFormatting.RANGE_SCALE_PERCENTAGE = '%';
	DeprecatedConditionalFormatting.RANGE_SCALE_NUMERIC = '#';

	return DeprecatedConditionalFormatting;
});
